import { Component, ErrorInfo, ReactNode } from "react";
import { Warning, ArrowsClockwise, House } from "@/components/ui/icons";
import { captureError } from "@/lib/sentry";
import { isRecoverableModuleLoadError } from "@/lib/moduleLoadErrors";
import i18n from "@/i18n";
import { supabase } from "@/integrations/supabase/client";

const CHUNK_ERROR_RETRY_KEY = "chunk-error-boundary-retry";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  isRecoveringToAuth: boolean;
}

const ERROR_BUTTON_BASE =
  "inline-flex h-11 items-center justify-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2";

const ERROR_BUTTON_OUTLINE =
  "border border-input bg-background text-foreground hover:bg-accent/10 hover:text-white hover:border-accent";

const ERROR_BUTTON_PRIMARY =
  "btn-primary text-zinc-950 hover:-translate-y-px active:translate-y-0";

/**
 * Checks if an error is related to chunk/module loading failures.
 * These typically occur after a deployment when cached index.html
 * references chunk hashes that no longer exist.
 */
function isChunkLoadError(error: Error): boolean {
  return isRecoverableModuleLoadError(error);
}

const PUBLIC_PATHS = new Set([
  "/",
  "/auth",
  "/terms",
  "/privacy",
  "/legal/dpa",
  "/legal/do-not-sell-or-share",
  "/mfa-verify",
  "/oauth/consent",
]);

function isPublicPath(pathname: string): boolean {
  return PUBLIC_PATHS.has(pathname);
}

function isAuthRecoveryCandidate(error: Error): boolean {
  const message = error.message.toLowerCase();
  return (
    message.includes("no active session") ||
    message.includes("auth session missing") ||
    message.includes("jwt") ||
    message.includes("before initialization")
  );
}

export class ErrorBoundary extends Component<Props, State> {
  private authRecoveryAttempted = false;

  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      isRecoveringToAuth: false,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    this.setState({ errorInfo });

    // Check if this is a chunk load error that we should auto-recover from
    if (isChunkLoadError(error)) {
      const hasRetried =
        sessionStorage.getItem(CHUNK_ERROR_RETRY_KEY) === "true";

      if (!hasRetried) {
        // First chunk error - auto-reload to get fresh index.html
        sessionStorage.setItem(CHUNK_ERROR_RETRY_KEY, "true");
        window.location.reload();
        return;
      }

      // Already retried - clear flag and show error UI
      sessionStorage.removeItem(CHUNK_ERROR_RETRY_KEY);
    }

    // Log to console in development
    console.error("ErrorBoundary caught an error:", error, errorInfo);

    // Send to Sentry for production error tracking
    captureError(error, {
      componentStack: errorInfo.componentStack,
      errorBoundary: true,
      isChunkLoadError: isChunkLoadError(error),
    });

    void this.tryRecoverToAuth(error);
  }

  private async tryRecoverToAuth(error: Error): Promise<void> {
    if (this.authRecoveryAttempted || typeof window === "undefined") {
      return;
    }

    if (isPublicPath(window.location.pathname)) {
      return;
    }

    if (!isAuthRecoveryCandidate(error)) {
      return;
    }

    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (session?.user) {
        return;
      }

      this.authRecoveryAttempted = true;
      this.setState({ isRecoveringToAuth: true });
      const redirectTarget = `${window.location.pathname}${window.location.search}${window.location.hash}`;
      const authUrl = new URL("/auth", window.location.origin);
      authUrl.searchParams.set("redirect", redirectTarget);
      window.location.replace(authUrl.toString());
    } catch {
      this.authRecoveryAttempted = true;
      const fallbackAuthUrl = new URL("/auth", window.location.origin);
      window.location.replace(fallbackAuthUrl.toString());
    }
  }

  handleReload = (): void => {
    window.location.reload();
  };

  handleGoHome = (): void => {
    window.location.href = "/dashboard";
  };

  handleTryAgain = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      isRecoveringToAuth: false,
    });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.state.isRecoveringToAuth) {
        return (
          <div className="min-h-screen flex items-center justify-center bg-background p-4">
            <p className="text-muted-foreground">Redirecting to login...</p>
          </div>
        );
      }

      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-screen flex items-center justify-center bg-background p-4">
          <div className="max-w-md w-full text-center space-y-6">
            <div className="mx-auto w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center">
              <Warning size={32} className="text-destructive" />
            </div>

            <div className="space-y-2">
              <h1 className="text-2xl font-bold text-foreground">
                {i18n.t("errorBoundary.title")}
              </h1>
              <p className="text-muted-foreground">
                {i18n.t("errorBoundary.description")}
              </p>
            </div>

            {process.env.NODE_ENV === "development" && this.state.error && (
              <div className="text-left bg-muted/50 rounded-lg p-4 overflow-auto max-h-40">
                <p className="text-sm font-mono text-destructive break-all">
                  {this.state.error.toString()}
                </p>
                {this.state.errorInfo && (
                  <pre className="mt-2 text-xs text-muted-foreground whitespace-pre-wrap">
                    {this.state.errorInfo.componentStack}
                  </pre>
                )}
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={this.handleTryAgain}
                className={`${ERROR_BUTTON_BASE} ${ERROR_BUTTON_OUTLINE}`}
              >
                <ArrowsClockwise size={16} />
                {i18n.t("errorBoundary.tryAgain")}
              </button>
              <button
                onClick={this.handleReload}
                className={`${ERROR_BUTTON_BASE} ${ERROR_BUTTON_OUTLINE}`}
              >
                <ArrowsClockwise size={16} />
                {i18n.t("errorBoundary.refreshPage")}
              </button>
              <button
                onClick={this.handleGoHome}
                className={`${ERROR_BUTTON_BASE} ${ERROR_BUTTON_PRIMARY}`}
              >
                <House size={16} />
                {i18n.t("errorBoundary.goToDashboard")}
              </button>
            </div>

            <p className="text-sm text-muted-foreground">
              {i18n.t("errorBoundary.contactSupport")}
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
